import React, { useState } from "react";
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  Grid,
  Pagination,
  Typography,
  createTheme,
  useMediaQuery,
} from "@mui/material";
import { Link } from "react-router-dom";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import WorkspacePremiumIcon from "@mui/icons-material/EmojiEvents";
import { useAnswersQuery } from "../../queries/useToeicQueries";

const Ranking = () => {
  const { data: answers = [] } = useAnswersQuery("");
  const [page, setPage] = useState(1);
  const itemsPerPage = 12;

  const indexOfLastItem = page * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = answers.slice(indexOfFirstItem, indexOfLastItem);

  const handleChangePage = (event, value) => {
    setPage(value);
  };

  const theme = createTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const boardPalettes = [
    { bg: "#fff7d6", color: "#b77900", border: "#f6d365", iconBg: "#ffe8a3" },
    { bg: "#f0ecff", color: "#6d4aff", border: "#c6b8ff", iconBg: "#ded6ff" },
    { bg: "#ffe8f0", color: "#d81b60", border: "#ffb8cf", iconBg: "#ffd0df" },
    { bg: "#e8fbff", color: "#0087a5", border: "#a7e9f6", iconBg: "#c9f4fb" },
    { bg: "#fff0db", color: "#d56b00", border: "#ffc57a", iconBg: "#ffdfad" },
    { bg: "#e8f9ed", color: "#1f9b45", border: "#a9eabc", iconBg: "#c7f3d2" },
  ];

  return (
    <Box
      sx={{
        mt: 4,
        mb: 6,
        width: "100%",
      }}
    >
      <Box component="header">
        <Box className="toeic-reveal">
          <Typography
            component="h1"
            sx={{
              m: 0,
              color: "var(--toeic-ink)",
              fontSize: { xs: 36, md: 56 },
              lineHeight: { xs: 1.05, md: 1 },
              fontWeight: 800,
              letterSpacing: 0,
            }}
          >
            <Box component="span" sx={{ position: "relative", display: "inline-block" }}>
              <Box
                component="span"
                aria-hidden="true"
                sx={{
                  position: "absolute",
                  left: "-0.12em",
                  right: "-0.12em",
                  bottom: "0.04em",
                  height: "0.44em",
                  bgcolor: "rgba(240, 184, 77, 0.34)",
                  borderRadius: "0.45em 0.6em 0.4em 0.55em",
                  transform: "rotate(-1deg)",
                }}
              />
              <Box component="span" sx={{ position: "relative" }}>
                TOEIC Ranking
              </Box>
            </Box>
            .
          </Typography>
        </Box>
        <Typography
          className="toeic-reveal"
          sx={{
            mt: 2,
            maxWidth: 660,
            color: "var(--toeic-ink-soft)",
            fontSize: 18,
            lineHeight: 1.65,
            fontWeight: 700,
          }}
        >
          Pick a test and open its leaderboard without the heavy dashboard frame.
        </Typography>
      </Box>

      <Box sx={{ mt: 4, mb: 4, display: "grid", gap: 4 }}>
        <Box aria-hidden="true" sx={{ borderTop: "1.5px dotted var(--toeic-line)" }} />
        <Box
          className="toeic-reveal"
        sx={{
          p: isMobile ? 2 : 2.5,
          borderRadius: "18px",
          border: "1px solid rgba(215, 234, 245, 0.82)",
          bgcolor: "var(--toeic-paper-grid)",
          boxShadow: "var(--toeic-shadow-card)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            gap: 1,
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              color: "var(--toeic-ink)",
              fontSize: 14,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: ".12em",
            }}
          >
            Leaderboard Boards
          </Typography>
          <Typography sx={{ color: "var(--toeic-ink-soft)", fontSize: 12, fontWeight: 700 }}>
            {answers.length} tests - top 10 scores
          </Typography>
        </Box>
        </Box>
        <Box aria-hidden="true" sx={{ borderTop: "1.5px dotted var(--toeic-line)" }} />
      </Box>

      <Grid container spacing={2}>
        {currentItems.map((answer, index) => {
          const displayIndex = indexOfFirstItem + index + 1;
          const palette = boardPalettes[index % boardPalettes.length];

          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={answer._id || answer.answer_name}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  border: 0,
                  borderRadius: "14px",
                  bgcolor: "var(--toeic-surface)",
                  boxShadow: "var(--toeic-shadow-card)",
                  overflow: "hidden",
                  transition: "transform 0.22s var(--toeic-ease-fluid), box-shadow 0.2s ease",
                  "&:hover": {
                    boxShadow: `0 24px 44px -30px rgba(19, 43, 63, 0.52)`,
                    transform: "translateY(-4px)",
                  },
                }}
              >
                <CardActionArea
                  component={Link}
                  to={`/ranking/${answer._id}`}
                  sx={{
                    height: "100%",
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Chip
                      size="small"
                      icon={<WorkspacePremiumIcon />}
                      label={`Board ${displayIndex}`}
                      sx={{
                        bgcolor: "rgba(70, 168, 232, 0.12)",
                        color: palette.color,
                        fontWeight: 700,
                        "& .MuiChip-icon": { color: palette.color },
                      }}
                    />
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "14px",
                        display: "grid",
                        placeItems: "center",
                        bgcolor: palette.iconBg,
                        color: palette.color,
                        flex: "0 0 auto",
                      }}
                    >
                      <LeaderboardIcon />
                    </Box>
                  </Box>

                  <Box sx={{ minHeight: 64 }}>
                    <Typography
                      sx={{
                        color: "var(--toeic-ink)",
                        fontWeight: 700,
                        lineHeight: 1.3,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {answer.answer_name}
                    </Typography>
                    <Typography sx={{ mt: 0.75, color: "var(--toeic-ink-soft)", fontSize: 14, fontWeight: 700 }}>
                      Highest attempt leaderboard
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      mt: "auto",
                      pt: 1.5,
                      borderTop: "1px dotted var(--toeic-line)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      color: palette.color,
                      fontWeight: 700,
                    }}
                  >
                    View ranking
                    <NavigateNextIcon fontSize="small" />
                  </Box>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {answers.length === 0 && (
        <Box
          sx={{
            p: 4,
            textAlign: "center",
            borderRadius: "18px",
            border: "1.5px dashed var(--toeic-line)",
            bgcolor: "var(--toeic-surface)",
          }}
        >
          <Typography sx={{ color: "var(--toeic-ink-soft)", fontWeight: 800 }}>No ranking board available</Typography>
        </Box>
      )}

      <Pagination
        count={Math.ceil(answers.length / itemsPerPage)}
        page={page}
        onChange={handleChangePage}
        sx={{ mt: 3, display: "flex", justifyContent: "center" }}
      />
    </Box>
  );
};

export default Ranking;
